import * as React from 'react'

function safeParse(raw, fallback) {
  try {
    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

function useLocalStorageState({
  key,
  initialValue,
  version = '1',
  serialize = JSON.stringify,
  deserialize,
}) {
  const initial = React.useMemo(
    () => (typeof initialValue === 'function' ? initialValue() : initialValue),
    [initialValue]
  )

  const read = React.useCallback(() => {
    if (typeof window === 'undefined') return initial

    const raw = window.localStorage.getItem(key)
    if (!raw) return initial

    const parsed = deserialize ? deserialize(raw) : safeParse(raw, { data: initial })
    const payload =
      parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'data')
        ? parsed
        : { __v: undefined, data: parsed }

    if (payload.__v && payload.__v !== version) return initial
    return payload.data != null ? payload.data : initial
  }, [key, version, deserialize, initial])

  const [state, setState] = React.useState(() => read())

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const payload = { __v: version, data: state }
    try {
      window.localStorage.setItem(key, serialize(payload))
    } catch {
      throw new Error(`Error setting localStorage key "${key}"`)
    }
  }, [key, state, version, serialize])

  const reset = React.useCallback(() => setState(initial), [initial])

  return [state, setState, reset]
}

export default useLocalStorageState
